/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Checkbox, Col, Collapse, Form, Input, Select, Typography, Tooltip, DatePicker, Row } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import LabelWithOriginalText from '../../../Forms/Fields/LabelWithOriginalText';
import RichTextEditor from '../../../Forms/Fields/RichTextEditor/RichTextEditor';
import FormSectionHeader from '../../../UI/FormSectionHeader';
import CustomInputNumber from '../../../Forms/Fields/CustomInputNumber';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { TEXT_INPUT_TYPES } from '../../../../constants/flowGraph';
import { useParams, useSearchParams } from 'react-router-dom';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import { ACTIVITY_TYPES, VARIABLE_TYPES } from '../../../../constants/main';

// Constant declarations
const { Option } = Select;

const UserInputQueryNodeForm = ({ form }) => {
  const { t } = useTranslation();
  const { isEditingPrimaryLanguage, botLanguages, currentBotLanguage, primaryBotLanguage } = useBotLanguage();
  const { orgId, tenantId } = useParams();
  const [searchParams] = useSearchParams();

  const { data: activity } = getCurrentQueryState('fetchActivity', { orgId, tenantId, activityId: searchParams.get('activity'), currentBotLanguage }, !searchParams.get('activity'));

  useEffect(() => {
    // Initialize renderAsCard as true if not already set (need to trigger form state update for field checks)
    if (form.getFieldValue(['data', 'renderAsCard']) === undefined) {
      form.setFieldsValue({
        data: {
          renderAsCard: true
        }
      });
    }
  }, [form]);

  return (
    <Fragment>

      <LabelWithOriginalText className="px-4" label={t('Question')} originalText={form.getFieldValue(['data', 'label', primaryBotLanguage])} />

      {
        botLanguages.map(lang => (
          <Form.Item
            className="px-4"
            label=""
            name={['data', 'label', lang]}
            hidden={currentBotLanguage !== lang}
            rules={[{ required: lang === primaryBotLanguage && isEditingPrimaryLanguage, message: t('Question text cannot be blank.') }]}
          >
            <RichTextEditor
              placeholder={t('Enter a question')}
              includedVariableTypes={activity?.type === ACTIVITY_TYPES.LIVECHAT_FLOW ? [VARIABLE_TYPES.LIVECHAT] : []}
            />
          </Form.Item>
        ))
      }

      <Form.Item
        className="px-4"
        label={t('Question Data Type')}
        name={['data', 'inputType']}
        rules={[{ required: true, message: t('Please select an input type.') }]}
      >
        <Select
          placeholder={t('Select an input type')}
          size="large"
          getPopupContainer={triggerNode => triggerNode.parentElement}
          disabled={!isEditingPrimaryLanguage}
          onChange={(value) => {
            // Reset validation fields
            form.setFieldsValue({
              data: {
                validationFallbackMessage: { [currentBotLanguage]: '' },
                timesToValidate: '',
                customRegex: '',
                validation: {
                  minDateType: value === TEXT_INPUT_TYPES.DATE ? 'none' : undefined,
                  maxDateType: value === TEXT_INPUT_TYPES.DATE ? 'none' : undefined,
                  minDate: undefined,
                  maxDate: undefined
                }
              }
            });
          }}
        >
          {
            Object.entries(TEXT_INPUT_TYPES).map(([key, value]) => (
              <Option key={key} value={value}>{t(value, { context: 'text input type' })}</Option>
            ))
          }
        </Select>
      </Form.Item>

      {
        form.getFieldValue(['data', 'inputType']) && form.getFieldValue(['data', 'inputType']) === TEXT_INPUT_TYPES.CUSTOM_REGEX ? (
          <Form.Item
            className="px-4 pb-2"
            label={<Typography.Text>
              {t('Regular Expression')}
              <Tooltip
                className="ml-1"
                placement="top"
                title={t('The agent will validate an input with the regular expression.')}
              >
                <QuestionCircleOutlined className="fs-sm position-absolute" />
              </Tooltip>
            </Typography.Text>}
            name={['data', 'customRegex']}
            rules={[
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.resolve();
                  }
                  try {
                    // Check for valid regular expression
                    new RegExp(value);
                    return Promise.resolve();
                  } catch (err) {
                    return Promise.reject(new Error(t('Please enter a valid regular expression.')));
                  }
                }
              }
            ]}
          >
            <Input size="large" placeholder={t('Enter an expression')} disabled={!isEditingPrimaryLanguage}/>
          </Form.Item>
        ) : null
      }

      {
        form.getFieldValue(['data', 'inputType']) && form.getFieldValue(['data', 'inputType']) !== TEXT_INPUT_TYPES.STRING ? (
          <Col className="ant-collapse-borderless px-4 py-2 border-radius-base">
            <Typography.Text type="secondary">{t('Validation')}</Typography.Text>

            {form.getFieldValue(['data', 'inputType']) === TEXT_INPUT_TYPES.DATE && (
              <>
                <Row gutter={8} className="mt-4">
                  <Col flex={'auto'}>
                    <Form.Item
                      label={t('Min Date')}
                      name={['data', 'minDate', 'type']}
                      tooltip={{
                        icon: <QuestionCircleOutlined />,
                        title: <Row>
                          <Col span={24}>
                            {t('The earliest allowed date')}:
                          </Col>
                          <Col>
                            {'• Session Date – Uses current session date at runtime'}
                          </Col>
                          <Col>
                            {'• Exact Date – Choose a specific earliest date'}
                          </Col>
                        </Row>
                      }}
                    >
                      <Select
                        placeholder={t('Select min date type')}
                        size="large"
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                        disabled={!isEditingPrimaryLanguage}
                        allowClear
                        onChange={() => {
                          form.setFieldsValue({
                            data: {
                              minDate: {
                                value: undefined
                              }
                            }
                          });
                        }}
                      >
                        <Option value="SESSION">{t('Session Date')}</Option>
                        <Option value="EXACT">{t('Exact Date')}</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  {form.getFieldValue(['data', 'minDate', 'type']) === 'EXACT' && (
                    <Col flex={'160px'}>
                      <Form.Item
                        name={['data', 'minDate', 'value']}
                        label=" "
                      >
                        <DatePicker
                          size="large"
                          className="w-100"
                          disabled={!isEditingPrimaryLanguage}
                        />
                      </Form.Item>
                    </Col>
                  )}
                </Row>

                <Row gutter={8}>
                  <Col flex={'auto'}>
                    <Form.Item
                      label={t('Max Date')}
                      name={['data', 'maxDate', 'type']}
                      tooltip={{
                        icon: <QuestionCircleOutlined />,
                        title: <Row>
                          <Col span={24}>
                            {t('The latest allowed date')}:
                          </Col>
                          <Col>
                            {'• Session Date – Uses current session date at runtime'}
                          </Col>
                          <Col>
                            {'• Exact Date – Choose a specific latest date'}
                          </Col>
                        </Row>
                      }}
                    >
                      <Select
                        placeholder={t('Select max date type')}
                        size="large"
                        allowClear
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                        disabled={!isEditingPrimaryLanguage}
                        onChange={() => {
                          form.setFieldsValue({
                            data: {
                              maxDate: {
                                value: undefined
                              }
                            }
                          });
                        }}
                      >
                        <Option value="SESSION">{t('Session Date')}</Option>
                        <Option value="EXACT">{t('Exact Date')}</Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  {
                    form.getFieldValue(['data', 'maxDate', 'type']) === 'EXACT' ? (
                      <Col flex="160px">
                        <Form.Item
                          name={['data', 'maxDate', 'value']}
                          label=" "
                        >
                          <DatePicker
                            size="large"
                            className="w-100"
                            disabled={!isEditingPrimaryLanguage}
                          />
                        </Form.Item>
                      </Col>
                    ) : null
                  }
                </Row>
              </>
            )}

            {
              botLanguages.map(lang => (
                <Form.Item
                  label={t('Invalid input message')}
                  name={['data', 'validationFallbackMessage', lang]}
                  hidden={currentBotLanguage !== lang}
                >
                  <Input
                    size="large"
                    placeholder={t('Enter a message for invalid input')}
                  />
                </Form.Item>
              ))
            }

            <Form.Item
              name={['data', 'timesToValidate']}
              label={
                <Typography.Text>
                  {t('Times to Validate')}
                  <Tooltip
                    className="ml-1"
                    placement="top"
                    title={t('How many times to validate an input before exiting the flow.')}
                  >
                    <QuestionCircleOutlined className="fs-sm position-absolute" />
                  </Tooltip>
                </Typography.Text>
              }
            >
              <CustomInputNumber
                placeholder="-"
                size="large"
                min={0}
                buttonClassName="bg-skeleton"
                disabled={!isEditingPrimaryLanguage}
              />
            </Form.Item>
          </Col>
        ) : null
      }

      <Collapse ghost>
        <Collapse.Panel
          className="ant-collapse-p-0 pb-2"
          showArrow={false}
          header={<FormSectionHeader title={t('Advanced Settings')} />}
          key="1"
        >
          <Form.Item
            name={['data', 'inputIsMandatory']}
            valuePropName="checked"
            className="mb-6 mt-2 px-4"
          >
            <Checkbox disabled={!isEditingPrimaryLanguage}>
              {t('Required Field')}
            </Checkbox>
          </Form.Item>

          <Col className="ant-collapse-borderless border-radius-base px-4 py-2  mb-4">
            <Typography.Text type="secondary">{t('Preferred Rendering')}</Typography.Text>

            <Form.Item
              className="mt-4"
              name={['data', 'renderAsCard']}
              valuePropName="checked"
            >
              <Checkbox
                disabled={!isEditingPrimaryLanguage}
                onChange={({ target }) => {
                  if (target?.checked === false) {
                    form.setFieldsValue({ data: { placeholder: { [currentBotLanguage]: '' } } });
                    form.setFieldsValue({ data: { inputMaxCharacters: '' } });
                    form.setFieldsValue({ data: { inputIsMultiLine: '' } });
                  }
                }}
              >
                <Typography.Text>
                  {t('Render as a card')}
                  <Tooltip
                    className="ml-1"
                    placement="top"
                    title={t('Render as a card choice can be applied only in Webchat and MS Teams deployments.')}
                  >
                    <QuestionCircleOutlined className="fs-sm position-absolute" />
                  </Tooltip>
                </Typography.Text>
              </Checkbox>
            </Form.Item>

            {
              botLanguages.map(lang => (
                <Form.Item
                  label={t('Placeholder text')}
                  name={['data', 'placeholder', lang]}
                  hidden={currentBotLanguage !== lang}
                >
                  <Input
                    placeholder={t('Enter placeholder keyword')}
                    disabled={!form.getFieldValue(['data', 'renderAsCard'])}
                    size="large"
                    maxLength={30}
                  />
                </Form.Item>
              ))
            }

            <Form.Item
              label={t('Maximum allowed characters')}
              name={['data', 'inputMaxCharacters']}
            >
              <CustomInputNumber
                placeholder={'-'}
                size="large"
                min={0}
                buttonClassName="bg-skeleton"
                disabled={!isEditingPrimaryLanguage || !form.getFieldValue(['data', 'renderAsCard'])}
              />
            </Form.Item>

            <Form.Item
              name={['data', 'inputIsMultiLine']}
              valuePropName="checked"
              className="mb-4"
            >
              <Checkbox disabled={!isEditingPrimaryLanguage || !form.getFieldValue(['data', 'renderAsCard'])}>
                {t('Display as multi-line')}
              </Checkbox>
            </Form.Item>
            <LabelWithOriginalText label={t('Submit button label')} originalText={form.getFieldValue(['data', 'submitLabel', primaryBotLanguage])} />
            {
              botLanguages.map(lang => (
                <Form.Item
                  label=""
                  name={['data', 'submitLabel', lang]}
                  hidden={currentBotLanguage !== lang}
                >
                  <Input
                    placeholder={t('Enter a submit label')}
                    size="large"
                    disabled={!form.getFieldValue(['data', 'renderAsCard'])}
                  />
                </Form.Item>
              ))
            }

            <LabelWithOriginalText label={t('Skip button label')} originalText={form.getFieldValue(['data', 'skipLabel', primaryBotLanguage])} />
            {
              botLanguages.map(lang => (
                <Form.Item
                  label=""
                  name={['data', 'skipLabel', lang]}
                  hidden={currentBotLanguage !== lang}
                >
                  <Input
                    placeholder={t('Enter a skip label')}
                    size="large"
                    disabled={!form.getFieldValue(['data', 'renderAsCard'])}
                  />
                </Form.Item>
              ))
            }
          </Col>

          {
            botLanguages.map(lang => (
              <Form.Item
                className="px-4"
                label={<Typography.Text>
                  {t('Exit Keyword to skip the question')}
                  <Tooltip
                    className="ml-1"
                    placement="top"
                    title={t('The end user can skip the question without answering and continue chatting with the agent by typing the exit keyword. They will be informed  about the exit keyword with a note following the question.')}
                  >
                    <QuestionCircleOutlined className="fs-sm position-absolute" />
                  </Tooltip>
                </Typography.Text>}
                name={['data', 'exitKeyWords', lang]}
                hidden={currentBotLanguage !== lang}
              >
                <Input
                  placeholder={t('Enter exit keyword')}
                  size="large"
                  maxLength={30}
                />
              </Form.Item>
            ))
          }

          <Form.Item
            className="px-4"
            label={<Typography.Text>
              {t('Variable name')}
              <Tooltip
                className="ml-1"
                placement="top"
                title={t('It is used for storing the answer to this question. Set a variable name to use the answer in messages templates, or in control flow nodes.')}
              >
                <QuestionCircleOutlined className="fs-sm position-absolute" />
              </Tooltip>
            </Typography.Text>}
            name={['data', 'questionId']}
            rules={[{
              pattern: /^[a-zA-Z0-9_$\\.]*$/,
              message: t('Variable names can only use letters, numbers, underscores, dollar signs, and dots.')
            }]}
          >
            <Input
              placeholder={t('Enter the variable name')}
              size="large"
              maxLength={30}
              disabled={!isEditingPrimaryLanguage}
            />
          </Form.Item>

        </Collapse.Panel>
      </Collapse>
    </Fragment>
  );
};

UserInputQueryNodeForm.propTypes = {
  form: PropTypes.object.isRequired
};

export default UserInputQueryNodeForm;
